import { Flex, Form, Input, notification } from "antd";
import { type FC, useCallback, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { getAddressInfo, getApiError, postAddressCreate } from "tm-shared/api";
import { Option } from "tm-shared/types";

import BaseButton from "@/components/BaseButton";
import { BaseModal } from "@/components/BaseModal";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch } from "@/hooks/redux";
import { useFormDependencies } from "@/hooks/useFormDependencies";
import {
    setAddressVerifyModalSelectionPayload,
    setMarketCreatePageModalSelectionPayload,
} from "@/store/slices/ui";
import type { NewAddressFormValues } from "@/types/form";
import { AddAddressModalPayload } from "@/types/modals";

import { FIELD_FLOW_MAP } from "./AddAddressModal.constants";
import { HouseNumberConstructor } from "./HouseNumberConstructor/HouseNumberConstructor";
import { SettlementField } from "./SettlementField";
import { StreetField } from "./StreetField";

type FieldName = keyof NewAddressFormValues;

interface AddAddressModalProps {
    onClose: () => void;
    payload: AddAddressModalPayload;
}

const emptyValues: NewAddressFormValues = {
    settlement: "",
    target: "",
    house: "",
    streetLabel: "",
    address: null,
    handleNumber: "",
    handleComponents: [],
};

export const AddAddressModal: FC<AddAddressModalProps> = ({
    onClose,
    payload,
}) => {
    const dispatch = useAppDispatch();

    const [selectMode, setSelectMode] = useState(true);

    const addAddressForm = useForm<NewAddressFormValues>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: emptyValues,
    });

    const { handleSubmit, resetField, control, formState } = addAddressForm;

    const setSelectionPayload = useCallback(
        ({ option, value }: { option: Option; value: string }) => {
            if (payload.variant === "verifyAddress") {
                dispatch(
                    setAddressVerifyModalSelectionPayload({
                        fieldName: "address",
                        value: value,
                        option: option,
                    }),
                );
            } else {
                dispatch(
                    setMarketCreatePageModalSelectionPayload({
                        fieldName: "address",
                        value: value,
                        option: option,
                    }),
                );
            }
        },
        [dispatch, payload.variant],
    );

    const onSubmit = useCallback(
        async (data: NewAddressFormValues) => {
            const { address, target, house, handleNumber, handleComponents } =
                data;

            if (address && selectMode) {
                const addressOption = {
                    label: address,
                    value: house,
                };

                setSelectionPayload({
                    value: addressOption.value,
                    option: addressOption,
                });
            } else {
                let newAddressId: string;

                try {
                    newAddressId = (
                        await postAddressCreate({
                            number: handleNumber,
                            area: payload.regionId,
                            target,
                            components: handleComponents,
                        })
                    ).result;
                } catch (error) {
                    const { code, displayMessage } = getApiError(error);

                    notification.error({
                        message: "Помилка при створенні адреси",
                        description:
                            code === "-32404"
                                ? "Адреса уже існує"
                                : displayMessage,
                    });

                    return;
                }

                const newAddressInfoResponse = await getAddressInfo({
                    address: newAddressId,
                });

                const addressOption = {
                    label: newAddressInfoResponse.result.name,
                    value: newAddressInfoResponse.result.id,
                };

                setSelectionPayload({
                    value: addressOption.value,
                    option: addressOption,
                });
            }

            onClose();
        },
        [dispatch, onClose, selectMode, payload],
    );

    const dependencyManager = useFormDependencies(FIELD_FLOW_MAP);

    const resetBelow = useCallback(
        (fieldName: FieldName) => {
            const fieldsToClear = dependencyManager.getFieldsToReset(fieldName);

            fieldsToClear.forEach((name) => {
                resetField(name, { defaultValue: emptyValues[name] });
            });
        },
        [dependencyManager, resetField],
    );

    const watchedValues = useWatch({
        control,
    });

    const activeMap = useMemo(() => {
        return dependencyManager.computeActiveMap(watchedValues);
    }, [dependencyManager, watchedValues]);

    return (
        <BaseModal
            open
            onClose={onClose}
            maskClosable={false}
            actions={null}
            title={"Додати адресу"}
        >
            <PrimaryText>
                Для додавання нової адреси, заповніть форму
            </PrimaryText>

            <FormProvider {...addAddressForm}>
                <Form
                    layout="vertical"
                    component="form"
                    onFinish={handleSubmit(onSubmit)}
                >
                    <Flex vertical gap={8} style={{ marginBottom: 16 }}>
                        <Input
                            value={payload.region}
                            disabled={true}
                            size="large"
                        />

                        <SettlementField
                            resetBelow={resetBelow}
                            activeMap={activeMap}
                            regionId={payload.regionId}
                        />

                        <StreetField
                            resetBelow={resetBelow}
                            activeMap={activeMap}
                            selectMode={selectMode}
                            setSelectMode={setSelectMode}
                        />

                        {!selectMode && (
                            <HouseNumberConstructor
                                onBack={() => setSelectMode(true)}
                            />
                        )}
                    </Flex>

                    <Flex vertical gap={8}>
                        <BaseButton
                            type="default"
                            size="large"
                            variant="outlined"
                            onClick={onClose}
                            disabled={formState.isSubmitting}
                        >
                            Скасувати
                        </BaseButton>

                        <BaseButton
                            size="large"
                            type="primary"
                            htmlType="submit"
                            loading={formState.isSubmitting}
                        >
                            Додати адресу
                        </BaseButton>
                    </Flex>
                </Form>
            </FormProvider>
        </BaseModal>
    );
};
